import React, { useEffect, useState } from "react";
import { History, TriangleAlert } from "lucide-react";
import {
  CampaignActivation,
  CampaignHistory,
  CampaignPriceSnapshot,
} from "../../model/Campaign";
import { fetchCampaignHistory } from "../../helpers/campaignHistoryApi";
import { eur, pct, marginTone, TONE } from "../../helpers/format";

const TARGET_LABELS: Record<string, string> = {
  NIPOSOM_MARGIN: "Niposom",
  RESELLER_MARGIN: "Reseller",
  BOTH: "Ambas",
};

function discountLabel(s: CampaignPriceSnapshot): string {
  if (s.promo_type === "DISCOUNT_PERCENTAGE" && s.discount_percentage != null) {
    return `${s.discount_percentage}%`;
  }
  if (s.promo_type === "DISCOUNT_VALUE" && s.discount_value != null) {
    return eur.format(s.discount_value);
  }
  if (s.promo_type === "FIXED_PVP" && s.fixed_pvp != null) {
    return `PVP fixo: ${eur.format(s.fixed_pvp)}`;
  }
  return "—";
}

function Money({ value }: { value: number | null | undefined }) {
  return <>{value != null ? eur.format(value) : "—"}</>;
}

function MarginCell({
  value,
  pctValue,
}: {
  value: number | null | undefined;
  pctValue: number | null | undefined;
}) {
  if (value == null) return <>—</>;
  const tone = marginTone(pctValue);
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${TONE[tone]}`}
    >
      {eur.format(value)}
      {pctValue != null && <span>· {pct.format(pctValue)}</span>}
    </span>
  );
}

function ActivationSection({ activation }: { activation: CampaignActivation }) {
  const windowLabel = `${new Date(activation.starts_at).toLocaleDateString(
    "pt-PT",
  )} → ${new Date(activation.ends_at).toLocaleDateString("pt-PT")}`;

  return (
    <section className="bg-white rounded-lg shadow p-4">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3">
        <h3 className="font-semibold">
          Ativada em {new Date(activation.activated_at).toLocaleString("pt-PT")}
        </h3>
        <span className="text-xs text-gray-500">
          por {activation.activated_by}
        </span>
        <span className="text-xs text-gray-500">Janela: {windowLabel}</span>
        {activation.deactivated_at && (
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
            Encerrada em{" "}
            {new Date(activation.deactivated_at).toLocaleDateString("pt-PT")}
          </span>
        )}
        {activation.is_global ? (
          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
            Global
          </span>
        ) : (
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
            {activation.reseller_erp_ref ?? "—"}
          </span>
        )}
        <span className="text-xs text-gray-500">
          {activation.items.length}{" "}
          {activation.items.length === 1 ? "composto" : "compostos"}
        </span>
      </header>

      <div className="overflow-x-auto -mx-4 px-4">
        <table className="w-full min-w-[64rem] text-sm">
          <thead>
            <tr className="border-b text-left">
              <th scope="col" className="pb-2 font-medium">
                Composto
              </th>
              <th scope="col" className="pb-2 font-medium text-right">
                PVP config.
              </th>
              <th scope="col" className="pb-2 font-medium text-right">
                PVP promo
              </th>
              <th scope="col" className="pb-2 font-medium">
                Desconto / PVP
              </th>
              <th scope="col" className="pb-2 font-medium">
                Alvo
              </th>
              <th scope="col" className="pb-2 font-medium text-right">
                Preço reseller
              </th>
              <th scope="col" className="pb-2 font-medium text-right">
                Margem Niposom
              </th>
              <th scope="col" className="pb-2 font-medium text-right">
                Margem Reseller
              </th>
              <th scope="col" className="pb-2 font-medium">
                ERP
              </th>
            </tr>
          </thead>
          <tbody>
            {activation.items.map((s) => {
              const target = s.discount_target
                ? TARGET_LABELS[s.discount_target] || s.discount_target
                : "—";
              const resellerPrice =
                s.promotional_reseller_price ?? s.configured_base_retail_price;
              const drifted = (s.mismatch_fields?.length ?? 0) > 0;
              // A null field is never compared, so "no mismatch" must not be read
              // as "verified": both the configured base AND the ERP reference must
              // be present before an `ok` verdict is shown.
              const comparable =
                s.configured_pvp_with_vat != null &&
                s.configured_pvp_without_vat != null &&
                s.configured_base_retail_price != null &&
                s.configured_base_cost_price != null;
              const erpComparable =
                s.erp_price_pvp_with_vat != null &&
                s.erp_price_pvp_without_vat != null &&
                s.erp_base_retail_price != null &&
                s.erp_base_cost_price != null;
              return (
                <tr key={s.compound_id} className="border-b">
                  <th scope="row" className="py-2 font-normal text-left">
                    {s.compound_name || s.compound_id}
                    {!s.base_captured && (
                      <span
                        className="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600"
                        title="Base lida do composto no momento da ativação (item anterior a esta versão)"
                      >
                        base não capturada
                      </span>
                    )}
                  </th>
                  <td className="py-2 text-right tabular-nums">
                    <Money value={s.configured_pvp_with_vat} />
                  </td>
                  <td className="py-2 text-right tabular-nums font-semibold text-green-700">
                    <Money value={s.promotional_pvp_with_vat} />
                  </td>
                  <td className="py-2">{discountLabel(s)}</td>
                  <td className="py-2">
                    {target}
                    {s.discount_target === "BOTH" && (
                      <span className="ml-1 text-xs text-gray-500">
                        (financiado pela Niposom)
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    <Money value={resellerPrice} />
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    <MarginCell
                      value={
                        s.niposom_promo_margin_value ?? s.niposom_margin_value
                      }
                      pctValue={
                        s.niposom_promo_margin_pct ?? s.niposom_margin_pct
                      }
                    />
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    <MarginCell
                      value={
                        s.reseller_promo_margin_value ?? s.reseller_margin_value
                      }
                      pctValue={
                        s.reseller_promo_margin_pct ?? s.reseller_margin_pct
                      }
                    />
                  </td>
                  <td className="py-2">
                    {!s.base_captured || !comparable || !erpComparable ? (
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600"
                        title="Base incompleta no momento do item; o alinhamento com o ERP não é verificável"
                      >
                        não verificável
                      </span>
                    ) : drifted ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-300">
                        <TriangleAlert
                          className="h-3.5 w-3.5"
                          aria-hidden="true"
                        />
                        desalinhado
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                        ok
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const CampaignHistoryTab: React.FC<{
  campaignId: string;
  active?: boolean;
}> = ({ campaignId, active = true }) => {
  const [history, setHistory] = useState<CampaignHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [itemFilter, setItemFilter] = useState("");

  useEffect(() => {
    setItemFilter("");
  }, [campaignId]);

  useEffect(() => {
    // Skip while the tab is hidden; refetch each time it becomes active so an
    // activation done on another tab is reflected here.
    if (!active) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchCampaignHistory(campaignId)
      .then((data) => {
        if (!cancelled) setHistory(data);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setError(err.response?.data?.message || "Erro ao carregar histórico");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [campaignId, active]);

  if (loading) {
    return (
      <p role="status" className="text-gray-500 py-8 text-center">
        A carregar histórico…
      </p>
    );
  }
  if (error) {
    return (
      <p role="alert" className="text-red-700 py-4">
        {error}
      </p>
    );
  }

  const activations = history?.activations ?? [];
  if (activations.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
        <History
          className="w-6 h-6 mx-auto mb-2 text-gray-400"
          aria-hidden="true"
        />
        Sem histórico de ativação. Os registos começam a partir da primeira
        ativação após esta versão.
      </div>
    );
  }

  const compounds = Array.from(
    new Map(
      activations.flatMap((a) =>
        a.items.map((i) => [i.compound_id, i.compound_name || i.compound_id]),
      ),
    ).entries(),
  ).sort((x, y) => x[1].localeCompare(y[1], "pt-PT"));

  const filtered = itemFilter
    ? activations
        .map((a) => ({
          ...a,
          items: a.items.filter((i) => i.compound_id === itemFilter),
        }))
        .filter((a) => a.items.length > 0)
    : activations;

  return (
    <div className="space-y-4">
      {compounds.length > 1 && (
        <label className="flex items-center gap-2 text-sm">
          <span className="font-medium text-gray-700">Composto</span>
          <select
            value={itemFilter}
            onChange={(e) => setItemFilter(e.target.value)}
            className="border rounded-lg px-3 py-1.5"
          >
            <option value="">Todos</option>
            {compounds.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </label>
      )}

      {filtered.length === 0 ? (
        <p className="text-gray-500 text-sm">
          Nenhum item corresponde ao filtro selecionado.
        </p>
      ) : (
        <div className="space-y-6">
          {filtered.map((activation) => (
            <ActivationSection key={activation.id} activation={activation} />
          ))}
        </div>
      )}
    </div>
  );
};

export default CampaignHistoryTab;
